import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';

interface ImagePreviewProps {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Position {
  x: number;
  y: number;
}

export function ImagePreview({ src, alt, isOpen, onClose }: ImagePreviewProps) {
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<Position | null>(null);
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialScale, setInitialScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const animationFrameRef = useRef<number>();
  const lastPositionRef = useRef<Position>({ x: 0, y: 0 });

  // Disable background scrolling when modal is open
  useEffect(() => {
    console.log('isOpen', isOpen);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none'; // Disable pull-to-refresh
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setOffsetY(0);
      setPosition({ x: 0, y: 0 });
      lastPositionRef.current = { x: 0, y: 0 };
    }
  }, [isOpen]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Calculate bounds for dragging
  const getBounds = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

    const containerRect = containerRef.current.getBoundingClientRect();
    const imageRect = imageRef.current.getBoundingClientRect();

    const scaledWidth = imageRect.width * scale;
    const scaledHeight = imageRect.height * scale;

    const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    return {
      minX: -maxX,
      maxX: maxX,
      minY: -maxY,
      maxY: maxY,
    };
  }, [scale]);

  // Constrain position within bounds
  const constrainPosition = useCallback(
    (pos: Position) => {
      const bounds = getBounds();
      return {
        x: Math.min(Math.max(pos.x, bounds.minX), bounds.maxX),
        y: Math.min(Math.max(pos.y, bounds.minY), bounds.maxY),
      };
    },
    [getBounds]
  );

  // Optimized position update using requestAnimationFrame
  const updatePosition = useCallback(
    (newPosition: Position) => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        const constrained = constrainPosition(newPosition);
        lastPositionRef.current = constrained;
        setPosition(constrained);
      });
    },
    [constrainPosition]
  );

  // Handle pinch-to-zoom
  const handlePinchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);

    setInitialPinchDistance(distance);
    setInitialScale(scale);
  };

  const handlePinchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2 || !initialPinchDistance) return;

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    const currentDistance = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );

    const newScale = Math.min(
      Math.max(1, (initialScale * currentDistance) / initialPinchDistance),
      3
    );
    setScale(newScale);
  };

  const handlePinchEnd = () => {
    setInitialPinchDistance(null);
    setInitialScale(1);
  };

  // Handle mouse/touch events for dragging
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (scale <= 1) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setDragStart({
      x: clientX - lastPositionRef.current.x,
      y: clientY - lastPositionRef.current.y,
    });
    setIsDragging(true);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !dragStart || scale <= 1) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const newPosition = {
      x: clientX - dragStart.x,
      y: clientY - dragStart.y,
    };

    updatePosition(newPosition);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDragStart(null);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  // Handle touch events for swipe down to close
  const handleTouchStart = (e: React.TouchEvent) => {
    if (scale > 1 || e.touches.length === 2) return;
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      handlePinchMove(e);
      return;
    }

    if (!isDragging || scale > 1) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    setOffsetY(Math.max(0, diff));
  };

  const handleTouchEnd = () => {
    if (scale > 1) return;
    setIsDragging(false);
    if (offsetY > 100) {
      onClose();
    } else {
      setOffsetY(0);
    }
    handlePinchEnd();
  };

  // Handle zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
      const delta = e.deltaY * -0.01;
      const newScale = Math.min(Math.max(1, scale + delta), 3);

      if (newScale !== scale) {
        setScale(newScale);
        if (newScale === 1) {
          updatePosition({ x: 0, y: 0 });
        }
      }
    },
    [scale, updatePosition]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      style={{
        opacity: Math.max(0, 1 - offsetY / 300),
      }}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      <div
        ref={containerRef}
        className="relative max-w-[90vw] max-h-[90vh] overflow-hidden touch-none select-none"
        onTouchStart={e => {
          if (e.touches.length === 2) {
            handlePinchStart(e);
          } else if (scale > 1) {
            handleDragStart(e);
          } else {
            handleTouchStart(e);
          }
        }}
        onTouchMove={e => {
          if (e.touches.length === 2) {
            handlePinchMove(e);
          } else if (scale > 1) {
            handleDragMove(e);
          } else {
            handleTouchMove(e);
          }
        }}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onWheel={handleWheel}
        style={{
          transform: `translateY(${offsetY}px)`,
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="w-full h-full object-contain will-change-transform"
          style={{
            transform: `translate3d(${position.x / scale}px, ${position.y / scale}px, 0) scale(${scale})`,
            transformOrigin: 'center',
            transition: isDragging ? 'none' : 'transform 200ms ease-out',
          }}
          draggable={false}
        />
      </div>
    </div>
  );
}
